import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import {
  Loader2,
  Plus,
  X,
  Calendar,
  DollarSign,
  FileText,
  Layers,
  Clipboard,
  Upload,
  XCircle,
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useLanguageStore } from '../../store/languageStore';
import translations from '../../translations';
import {
  addExpense,
  getExpenseCategories,
  getWorkOrders as getOnlineWorkOrders,
} from '../../utils/api';
import {
  getOfflineCategories,
  saveCategories,
  getWorkOrders as getOfflineWorkOrders,
  saveWorkOrders,
  addPendingAction,
} from '../../utils/offlineDb';
import {
  sendTelegramMessage,
  sendTelegramFile,
  formatExpenseMessage,
} from '../../utils/telegram';
import { format } from 'date-fns';
import { useThemeStore } from '../../store/themeStore';

interface Category {
  id: number;
  name: string;
}

interface WorkOrder {
  report_id: number;
  wo_number: string;
  address?: string;
  city?: string;
  state?: string;
  status?: string; // Added status field
}

interface AddExpenseFormProps {
  onSuccess: () => void;
  onClose: () => void;
}

export function AddExpenseForm({ onSuccess, onClose }: AddExpenseFormProps) {
  const [loading, setLoading] = useState(false);
  const { language } = useLanguageStore();
  const t = translations[language];
  const [amount, setAmount] = useState('');
  const [comment, setComment] = useState('');
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [action, setAction] = useState<'SPENDING' | 'PENALTY'>('SPENDING');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Category and Work Order states
  const [categories, setCategories] = useState<Category[]>([]);
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>(
    undefined
  );
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<
    number | undefined
  >(undefined);

  const { accessToken, workerId, workerInfo } = useAuthStore();
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  const { isDark } = useThemeStore();

  // Filter function to exclude completed work orders
  const filterActiveWorkOrders = (orders: WorkOrder[]) => {
    return orders.filter((order) => {
      // Filter out completed work orders
      const completedStatuses = ['COMPLETED', 'REJECTED'];
      return (
        !order.status || !completedStatuses.includes(order.status.toUpperCase())
      );
    });
  };

  // Fetch categories and work orders
  useEffect(() => {
    const fetchData = async () => {
      if (!accessToken || !workerId) return;

      try {
        if (!navigator.onLine) {
          // Get categories from offline storage
          const offlineCategories = await getOfflineCategories();
          const activeCategories = (offlineCategories || []).filter(
            (cat) => cat.isActive
          );
          setCategories(activeCategories);

          // Get work orders from offline storage and filter out completed ones
          if (workerId) {
            const offlineOrders = await getOfflineWorkOrders();
            const filteredOrders = filterActiveWorkOrders(offlineOrders || []);
            setWorkOrders(filteredOrders);
          }
        } else {
          // Fetch expense categories
          const categoriesResponse = await getExpenseCategories(accessToken);
          const activeCategories = (categoriesResponse.data || []).filter(
            (cat) => cat.isActive
          );
          setCategories(activeCategories);
          // Save categories to offline storage
          await saveCategories(categoriesResponse.data || []);

          // Fetch work orders if workerId exists
          try {
            const workOrdersResponse = await getOnlineWorkOrders(
              accessToken,
              workerId
            );
            const allOrders = workOrdersResponse.orders || [];

            // Filter out completed work orders before setting state
            const activeOrders = filterActiveWorkOrders(allOrders);
            setWorkOrders(activeOrders);

            // Save all work orders to offline storage (including completed ones for other purposes)
            await saveWorkOrders(allOrders);
          } catch (error) {
            console.error('Failed to fetch work orders:', error);
            throw error; // Re-throw to be caught by outer catch block
          }
        }
      } catch (error) {
        console.error('Failed to fetch categories or work orders', error);
        toast.error('Failed to load data. Please try again.');
      }
    };

    fetchData();
  }, [accessToken, workerId, isOffline]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (selectedFiles.length + files.length > 5) {
      toast.error('Maximum 5 files allowed');
      return;
    }

    // Check each file size
    const oversizedFiles = files.filter((file) => file.size > 10 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      toast.error('Each file must be less than 10MB');
      return;
    }

    setSelectedFiles((prev) => [...prev, ...files]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accessToken || !workerId || !workerInfo) return;

    setLoading(true);
    try {
      const expenseAmount = parseFloat(amount);
      const expenseData = {
        workerId,
        amount: expenseAmount,
        comment: comment || `${action} expense`,
        date: new Date(date).toISOString(),
        categoryId: selectedCategory,
        orderId: selectedWorkOrder,
        action,
        files: selectedFiles.length > 0 ? selectedFiles : undefined,
        categoryName: categories.find((cat) => cat.id === selectedCategory)
          ?.name,
        woNumber: workOrders.find((wo) => wo.report_id === selectedWorkOrder)
          ?.wo_number,
      };

      if (!navigator.onLine) {
        // Add to pending actions
        await addPendingAction({
          type: 'addExpense',
          data: expenseData,
        });
        toast.success('Expense saved offline. Will sync when back online.');
        onSuccess();
        onClose();
        return;
      } else {
        await addExpense(
          accessToken,
          workerId,
          expenseAmount,
          comment || `${action} expense`,
          expenseData.date,
          selectedCategory,
          selectedWorkOrder,
          action,
          selectedFiles.length > 0 ? selectedFiles : undefined
        );
      }

      // Send Telegram notification
      if (workerInfo.groupId && navigator.onLine) {
        try {
          // Get category and work order details
          const selectedCategoryName = categories.find(
            (cat) => cat.id === selectedCategory
          )?.name;
          const selectedWorkOrderNumber = workOrders.find(
            (wo) => wo.report_id === selectedWorkOrder
          )?.wo_number;

          // Create enhanced worker info with expense details
          const enhancedWorkerInfo = {
            ...workerInfo,
            categoryName: selectedCategoryName,
            woNumber: selectedWorkOrderNumber,
            files: selectedFiles.map((file) => ({
              fileName: file.name,
              fileSize: file.size,
            })),
          };

          // First send the message
          const message = formatExpenseMessage(
            enhancedWorkerInfo,
            expenseAmount,
            comment
          );
          await sendTelegramMessage(workerInfo.groupId, message);

          // Then send each file
          if (selectedFiles.length > 0) {
            for (const file of selectedFiles) {
              const caption =
                `📎 <b>Expense Attachment</b>\n\n` +
                `<b>File:</b> ${file.name}\n` +
                `<b>Size:</b> ${(file.size / 1024).toFixed(1)}KB\n` +
                `<b>Category:</b> ${selectedCategoryName || 'N/A'}\n` +
                `<b>Work Order:</b> ${
                  selectedWorkOrderNumber
                    ? `#${selectedWorkOrderNumber}`
                    : 'N/A'
                }`;
              await sendTelegramFile(workerInfo.groupId, file, caption);
            }
          }
        } catch (error) {
          console.error('Failed to send Telegram notification:', error);
          // Don't throw error here to allow expense to be saved even if notification fails
        }
      }

      toast.success('Expense added successfully');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error('Failed to add expense');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-light-card dark:bg-dark-card w-full h-full sm:h-auto sm:max-h-[90vh] sm:w-full sm:max-w-md sm:rounded-lg p-4 sm:p-6 overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {t.expenses.form.title}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1 rounded-lg transition-colors"
            disabled={loading}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label
              htmlFor="date"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t.expenses.form.date}
            </label>
            <div className="relative w-full">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 h-5 w-5" />
              <input
                type="date"
                id="date"
                required
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className={`input pl-10 pr-4 py-3 w-full ${
                  isDark ? '[color-scheme:dark]' : '[color-scheme:light]'
                }`}
                max={format(new Date(), 'yyyy-MM-dd')}
                disabled={loading}
                style={{
                  paddingRight: '1rem',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                }}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label
              htmlFor="category"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t.expenses.form.category}
            </label>
            <div className="relative">
              <Layers className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 h-5 w-5" />
              <select
                id="category"
                value={selectedCategory || ''}
                onChange={(e) =>
                  setSelectedCategory(
                    e.target.value ? parseInt(e.target.value) : undefined
                  )
                }
                className="input pl-10 py-2.5 w-full"
                disabled={loading}
              >
                <option value="">{t.expenses.form.selectCategory}</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <label
              htmlFor="workOrder"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t.expenses.form.workOrder}
            </label>
            <div className="relative">
              <Clipboard className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 h-5 w-5" />
              <select
                id="workOrder"
                value={selectedWorkOrder || ''}
                onChange={(e) =>
                  setSelectedWorkOrder(
                    e.target.value ? parseInt(e.target.value) : undefined
                  )
                }
                className="input pl-10 py-2.5 w-full"
                disabled={loading}
              >
                <option value="">{t.expenses.form.selectWorkOrder}</option>
                {/* Only active (non-completed) work orders are shown here */}
                {workOrders.map((order) => (
                  <option key={order.report_id} value={order.report_id}>
                    {order.wo_number}{' '}
                    {order.address &&
                      `- ${order.address}, ${order.city}, ${order.state}`}
                  </option>
                ))}
              </select>
            </div>
            {workOrders.length === 0 && !loading && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                No active work orders available
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label
              htmlFor="action"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t.expenses.form.expenseType}
            </label>
            <select
              id="action"
              value={action}
              onChange={(e) =>
                setAction(e.target.value as 'SPENDING' | 'PENALTY')
              }
              className="input py-2.5 w-full"
              disabled={loading}
            >
              <option value="SPENDING">{t.expenses.form.spending}</option>
              <option value="PENALTY">{t.expenses.form.penalty}</option>
            </select>
          </div>

          <div className="space-y-2">
            <label
              htmlFor="amount"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t.expenses.form.amount}
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 h-5 w-5" />
              <input
                type="number"
                id="amount"
                step="0.01"
                min="0"
                required
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="input pl-10 py-3 w-full"
                placeholder="0.00"
                disabled={loading}
                style={{
                  paddingRight: '1rem',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                }}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label
              htmlFor="comment"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t.expenses.form.description}
            </label>
            <div className="relative">
              <FileText className="absolute left-3 top-3 text-gray-500 dark:text-gray-400 h-5 w-5" />
              <textarea
                id="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="input pl-10 min-h-[120px] resize-none py-3 w-full"
                placeholder="Enter expense description..."
                disabled={loading}
                style={{
                  paddingRight: '1rem',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                }}
                maxLength={500}
              />
              <div className="absolute bottom-2 right-2 text-xs text-gray-500 dark:text-gray-400">
                {comment.length}/500
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t.expenses.form.attachments}
            </label>
            <div className="relative w-full">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                className="hidden"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                multiple
                disabled={loading}
              />
              <div className="space-y-3">
                {selectedFiles.length > 0 && (
                  <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-900/20 rounded-lg p-3 w-full">
                    <div className="flex flex-col gap-2 w-full">
                      {selectedFiles.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center gap-2 truncate">
                            <FileText className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                            <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                              {file.name}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedFiles((files) =>
                                files.filter((_, i) => i !== index)
                              );
                            }}
                            className="text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400"
                          >
                            <XCircle className="h-5 w-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {selectedFiles.length < 5 && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-primary-500 dark:hover:border-primary-500 transition-colors"
                    disabled={loading}
                  >
                    <Upload className="h-5 w-5" />
                    <span>{t.expenses.form.uploadFile}</span>
                  </button>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t.expenses.form.maxFileSize}{' '}
                  {t.expenses.form.supportedFormats}
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary w-full sm:w-auto px-5 py-3"
              disabled={loading}
            >
              {t.expenses.buttons.cancel}
            </button>
            <button
              type="submit"
              className="btn-primary w-full sm:w-auto px-5 py-3"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t.expenses.buttons.adding}
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  {t.expenses.buttons.add}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
